import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import {
	Modal,
	Button,
	Alert,
	Badge,
	Offcanvas,
	Collapse,
	Form,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faSpinner,
	faPlusCircle,
	faCalendarAlt,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment-timezone";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Import custom hook
import { useEventsData } from "./eventsData";

// Import modals
import CreateEventModal from "./modals/CreateEventModal";
import EventDetailsModal from "./modals/EventDetailsModal";
import DeleteConfirmModal from "./modals/DeleteConfirmModal";

// Set up the moment localizer for react-big-calendar
const localizer = momentLocalizer(moment);

// List of common US IANA time zones
const timeZoneOptions = [
	{ value: "America/New_York", label: "Eastern Time (America/New_York)" },
	{ value: "America/Chicago", label: "Central Time (America/Chicago)" },
	{ value: "America/Denver", label: "Mountain Time (America/Denver)" },
	{ value: "America/Los_Angeles", label: "Pacific Time (America/Los_Angeles)" },
	{ value: "America/Anchorage", label: "Alaska Time (America/Anchorage)" },
	{ value: "Pacific/Honolulu", label: "Hawaii Time (Pacific/Honolulu)" },
	{ value: "UTC", label: "Coordinated Universal Time (UTC)" },
];

const Calendar = ({ isEmbedded }) => {
	// Get all state and handlers from custom hook
	const {
		events,
		loading,
		error,
		organizations,
		filterCriteria,
		openFilter,
		setOpenFilter,
		isFiltered,
		currentView,
		setCurrentView,
		currentDate,
		setCurrentDate,
		showDetailModal,
		isEditing,
		setIsEditing,
		selectedEvent,
		detailedEventData,
		detailLoading,
		editFormData,
		updateError,
		updateLoading,
		showCreateModal,
		createFormData,
		createError,
		createLoading,
		userRole,
		userOrgID,
		authLoading,
		showDeleteConfirm,
		deleteLoading,
		deleteError,
		getImageUrl,
		canEditEvent,
		canCreateEvent,
		handleFilterChange,
		handleApplyFilters,
		handleClearFilters,
		handleShowCreateModal,
		handleCloseCreateModal,
		handleCreateFormChange,
		handleCreateSubmit,
		handleSelectEvent,
		handleCloseDetailModal,
		handleEditEvent,
		handleEditFormChange,
		handleUpdateSubmit,
		handleShowDeleteConfirm,
		handleCloseDeleteConfirm,
		handleDeleteSubmit,
		handleSelectSlot,
		serviceTypes,
		serviceTypesLoading,
		serviceTypesError,
	} = useEventsData();

	const [showOffcanvas, setShowOffcanvas] = React.useState(false);
	const handleCloseOffcanvas = () => setShowOffcanvas(false);
	const handleShowOffcanvas = () => setShowOffcanvas(true);

	const [signInNavigator, setSignInNavigator] = React.useState("/signin");

	// Custom components for the calendar
	const AgendaEvent = ({ event }) => (
		<div style={{ fontWeight: "bold" }}>{event.title}</div>
	);

	const Event = ({ event }) => (
		<div className="rounded-sm">
			<strong className="" style={{ fontSize: "0.8rem" }}>
				{event.title}
			</strong>
		</div>
	);

	const eventPropGetter = useCallback(
		(event, start, end, isSelected) => {
			if (currentView === "month") {
				return {
					className: "",
					style: {
						cursor: "pointer",
					},
				};
			}
			return {
				style: {
					cursor: "pointer",
				},
			};
		},
		[currentView],
	);

	const CustomToolbar = (toolbar) => {
		const goToBack = () => {
			toolbar.onNavigate("PREV");
		};

		const goToNext = () => {
			toolbar.onNavigate("NEXT");
		};
		const goToCurrent = () => {
			toolbar.onNavigate("TODAY");
		};
		const label = () => {
			const date = moment(toolbar.date);
			let formattedLabel;

			if (toolbar.view === "day") {
				formattedLabel = date.format("dddd, MMMM D, YYYY");
			} else if (toolbar.view === "week") {
				const startOfWeek = moment(toolbar.date).startOf("week");
				const endOfWeek = moment(toolbar.date).endOf("week");
				const isSameYear = startOfWeek.year() === endOfWeek.year();
				const startFormat = isSameYear ? "MMM D" : "MMM D, YYYY";
				formattedLabel = `${startOfWeek.format(startFormat)} - ${endOfWeek.format("MMM D, YYYY")}`;
			} else if (toolbar.view === "agenda") {
				const startDate = moment(toolbar.date).startOf("day");
				const endDate = moment(toolbar.date).add(30, "days").endOf("day");
				const isSameYear = startDate.year() === endDate.year();
				const startFormat = isSameYear ? "MMM D" : "MMM D, YYYY";
				formattedLabel = `${startDate.format(startFormat)} - ${endDate.format("MMM D, YYYY")}`;
			} else {
				formattedLabel = date.format("MMMM YYYY");
			}

			return (
				<h2 className="mb-0 text-primary">
					<i className="bi bi-calendar-check-fill me-2"></i>
					{formattedLabel}
				</h2>
			);
		};

		const setView = (view) => {
			toolbar.onView(view);
		};

		return (
			<div className="rbc-toolbar row mb-3 align-items-center">
				<div className="col-auto">
					<Button variant="primary" onClick={goToBack} className="me-2">
						<i className="bi bi-chevron-left"></i>
					</Button>
					<Button
						variant="outline-primary"
						onClick={goToCurrent}
						className="me-2"
					>
						Today
					</Button>
					<Button variant="primary" onClick={goToNext}>
						<i className="bi bi-chevron-right"></i>
					</Button>
				</div>

				<div className="col text-center my-3">{label()}</div>

				<div className="col-auto d-flex justify-content-end">
					<Button
						variant={toolbar.view === "month" ? "primary" : "outline-primary"}
						onClick={() => setView("month")}
						className="rounded-end-0"
					>
						Month
					</Button>
					<Button
						variant={toolbar.view === "week" ? "primary" : "outline-primary"}
						onClick={() => setView("week")}
						className="rounded-0 border-start-0 border-end-0"
					>
						Week
					</Button>
					<Button
						variant={toolbar.view === "day" ? "primary" : "outline-primary"}
						onClick={() => setView("day")}
						className="rounded-start-0"
					>
						Day
					</Button>
					<Button
						variant={toolbar.view === "agenda" ? "primary" : "outline-primary"}
						onClick={() => setView("agenda")}
						className="rounded-start-0 border-start-0"
					>
						Agenda
					</Button>
				</div>
			</div>
		);
	};

	if (loading || authLoading) {
		return (
			<div
				className="d-flex justify-content-center align-items-center"
				style={{ minHeight: "80vh" }}
			>
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					size="3x"
					className="text-primary"
				/>
				<span className="ms-3 h4">Loading Calendar and Permissions...</span>
			</div>
		);
	}

	const userCanCreate = canCreateEvent(userRole);

	return (
		<>
			{/* Sidebar - Only if not embedded */}
			{!isEmbedded && (
				<Offcanvas
					show={showOffcanvas}
					onHide={handleCloseOffcanvas}
					placement="start"
					className="sidebar-default sidebar-white sidebar-base navs-rounded-all"
					style={{ width: "280px" }}
				>
					<Offcanvas.Header closeButton>
						<Offcanvas.Title>
							<i className="bi bi-list me-2"></i>Navigation Menu
						</Offcanvas.Title>
					</Offcanvas.Header>
					<Offcanvas.Body className="p-0">
						<aside className="h-100">
							<div
								className="sidebar-body pt-0 data-scrollbar"
								data-scrollbar="true"
								tabIndex={-1}
								style={{ overflowY: "auto", outline: "none", flex: 1 }}
							>
								<div className="scroll-content">
									<div className="sidebar-list">
										<ul className="navbar-nav iq-main-menu" id="sidebar-menu">
											<li className="nav-item">
												<a className="nav-link" href="/">
													<i className="bi bi-house-fill"></i>
													<span className="item-name">Home</span>
												</a>
											</li>
											<li className="nav-item">
												<a className="nav-link" href="/calendar">
													<i className="bi bi-calendar-event-fill"></i>
													<span className="item-name">Calendar</span>
												</a>
											</li>
											<li className="nav-item">
												<a className="nav-link" href="/bulletinboard">
													<i className="bi bi-pin-angle-fill"></i>
													<span className="item-name">Bulletin Board</span>
												</a>
											</li>
										</ul>
									</div>
								</div>
							</div>
						</aside>
					</Offcanvas.Body>
				</Offcanvas>
			)}

			<div className="container-fluid py-4">
				<div className="row">
					<div className="col-12">
						{error && <Alert variant="danger">{error}</Alert>}

						{/* Header with Create and Filter buttons */}
						<div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-white rounded shadow-sm">
							<h3 className="m-0">
								<i className="bi bi-calendar-event me-2 text-primary"></i>
								Community Calendar
							</h3>
							<div className="d-flex align-items-center gap-3">
								{isFiltered && (
									<Badge pill bg="success" className="p-2">
										<i className="bi bi-funnel-fill me-1"></i>Filters Active
									</Badge>
								)}

								{userCanCreate && (
									<Button
										variant="primary"
										onClick={handleShowCreateModal}
										size="sm"
									>
										<FontAwesomeIcon icon={faPlusCircle} className="me-2" />
										Create Event
									</Button>
								)}

								<Button
									onClick={() => setOpenFilter(!openFilter)}
									aria-controls="filterCollapse"
									aria-expanded={openFilter}
									variant="outline-secondary"
									size="sm"
								>
									<i className={`bi bi-sliders me-2`}></i>
									{openFilter ? "Hide Filters" : "Show Filters"}
								</Button>
							</div>
						</div>

						{/* Filter Section */}
						<Collapse in={openFilter}>
							<div
								id="filterCollapse"
								className="p-3 mb-3 bg-light rounded shadow-sm border"
							>
								<Form onSubmit={handleApplyFilters}>
									<div className="row g-3 mb-3">
										<div className="col-md-6">
											<Form.Group className="form-floating">
												<Form.Control
													type="text"
													id="title"
													name="title"
													value={filterCriteria.title}
													onChange={handleFilterChange}
													placeholder="Enter event title"
													style={{ minHeight: "57px" }}
												/>
												<Form.Label htmlFor="title">Event Title</Form.Label>
											</Form.Group>
										</div>
										<div className="col-md-6">
											<Form.Group className="form-floating">
												<Form.Select
													className="form-select form-select-floating pb-0 pt-3 ps-3"
													id="orgID"
													name="orgID"
													value={filterCriteria.orgID}
													onChange={handleFilterChange}
												>
													<option value="">All Organizations</option>
													{organizations.map((org) => (
														<option key={org.orgID} value={org.orgID}>
															{org.name}
														</option>
													))}
												</Form.Select>
												<Form.Label htmlFor="orgID">
													Filter by Organization{" "}
													<FontAwesomeIcon icon={faUsers} className="ms-1" />
												</Form.Label>
											</Form.Group>
										</div>
									</div>

									<div className="row g-3 mb-3">
										<div className="col-md-3">
											<Form.Group className="form-floating">
												<Form.Control
													type="date"
													id="startDate"
													name="startDate"
													value={filterCriteria.startDate}
													onChange={handleFilterChange}
													placeholder="YYYY-MM-DD"
												/>
												<Form.Label htmlFor="startDate">Start Date</Form.Label>
											</Form.Group>
										</div>

										<div className="col-md-3">
											<Form.Group className="form-floating">
												<Form.Control
													type="date"
													id="endDate"
													name="endDate"
													value={filterCriteria.endDate}
													onChange={handleFilterChange}
													placeholder="YYYY-MM-DD"
												/>
												<Form.Label htmlFor="endDate">End Date</Form.Label>
											</Form.Group>
										</div>

										<div className="col-md-3">
											<Form.Group className="form-floating">
												<Form.Control
													type="text"
													id="city"
													name="city"
													value={filterCriteria.city}
													onChange={handleFilterChange}
													placeholder="e.g., Chicago"
													style={{ minHeight: "57px" }}
												/>
												<Form.Label htmlFor="city">City</Form.Label>
											</Form.Group>
										</div>

										<div className="col-md-3">
											<Form.Group className="form-floating">
												<Form.Select
													id="createState"
													name="state"
													value={createFormData.state}
													onChange={handleCreateFormChange}
													required
													style={{
														minHeight: "57px",
														paddingLeft: "1rem",
													}}
												>
													<option value="">Select State</option>
													<option value="AL">Alabama</option>
													<option value="AK">Alaska</option>
													<option value="AZ">Arizona</option>
													<option value="AR">Arkansas</option>
													<option value="CA">California</option>
													<option value="CO">Colorado</option>
													<option value="CT">Connecticut</option>
													<option value="DE">Delaware</option>
													<option value="FL">Florida</option>
													<option value="GA">Georgia</option>
													<option value="HI">Hawaii</option>
													<option value="ID">Idaho</option>
													<option value="IL">Illinois</option>
													<option value="IN">Indiana</option>
													<option value="IA">Iowa</option>
													<option value="KS">Kansas</option>
													<option value="KY">Kentucky</option>
													<option value="LA">Louisiana</option>
													<option value="ME">Maine</option>
													<option value="MD">Maryland</option>
													<option value="MA">Massachusetts</option>
													<option value="MI">Michigan</option>
													<option value="MN">Minnesota</option>
													<option value="MS">Mississippi</option>
													<option value="MO">Missouri</option>
													<option value="MT">Montana</option>
													<option value="NE">Nebraska</option>
													<option value="NV">Nevada</option>
													<option value="NH">New Hampshire</option>
													<option value="NJ">New Jersey</option>
													<option value="NM">New Mexico</option>
													<option value="NY">New York</option>
													<option value="NC">North Carolina</option>
													<option value="ND">North Dakota</option>
													<option value="OH">Ohio</option>
													<option value="OK">Oklahoma</option>
													<option value="OR">Oregon</option>
													<option value="PA">Pennsylvania</option>
													<option value="RI">Rhode Island</option>
													<option value="SC">South Carolina</option>
													<option value="SD">South Dakota</option>
													<option value="TN">Tennessee</option>
													<option value="TX">Texas</option>
													<option value="UT">Utah</option>
													<option value="VT">Vermont</option>
													<option value="VA">Virginia</option>
													<option value="WA">Washington</option>
													<option value="WV">West Virginia</option>
													<option value="WI">Wisconsin</option>
													<option value="WY">Wyoming</option>
												</Form.Select>
												<Form.Label htmlFor="createState">State</Form.Label>
											</Form.Group>
										</div>
									</div>

									<div className="row g-3 mb-3">
										<div className="col-12">
											<Form.Group className="form-floating">
												<Form.Control
													type="text"
													id="locationName"
													name="locationName"
													value={filterCriteria.locationName}
													onChange={handleFilterChange}
													placeholder="e.g., library, park, church"
													style={{ minHeight: "57px" }}
												/>
												<Form.Label htmlFor="locationName">
													Specific Location Name
												</Form.Label>
											</Form.Group>
										</div>
									</div>

									<div className="d-flex justify-content-end gap-2">
										<Button
											variant="secondary"
											onClick={handleClearFilters}
											type="button"
										>
											<i className="bi bi-x-circle me-1"></i>Clear Filters
										</Button>
										<Button variant="primary" type="submit">
											<i className="bi bi-search me-1"></i>Apply Filters
										</Button>
									</div>
								</Form>
							</div>
						</Collapse>

						{/* Calendar */}
						<div
							style={{ height: "80vh" }}
							className="shadow-lg p-3 bg-white rounded"
						>
							<BigCalendar
								localizer={localizer}
								events={events}
								date={currentDate}
								view={currentView}
								onNavigate={setCurrentDate}
								onView={setCurrentView}
								selectable
								onSelectSlot={handleSelectSlot}
								dayLayoutAlgorithm="no-overlap"
								startAccessor="start"
								endAccessor="end"
								titleAccessor="title"
								style={{ minHeight: "100%" }}
								views={["month", "week", "day", "agenda"]}
								onSelectEvent={handleSelectEvent}
								showMoreThreshold={5}
								eventPropGetter={eventPropGetter}
								components={{
									toolbar: CustomToolbar,
									event: Event,
									agenda: {
										event: AgendaEvent,
									},
								}}
								className="cursor-pointer"
							/>
						</div>
					</div>
				</div>

				{/* Event Details Modal */}
				<EventDetailsModal
					show={showDetailModal}
					onHide={handleCloseDetailModal}
					isEditing={isEditing}
					setIsEditing={setIsEditing}

					detailedEventData={detailedEventData}
					detailLoading={detailLoading}
					editFormData={editFormData}
					handleEditFormChange={handleEditFormChange}
					handleUpdateSubmit={handleUpdateSubmit}
					updateError={updateError}
					updateLoading={updateLoading}
					getImageUrl={getImageUrl}
					canEditEvent={canEditEvent}
					userRole={userRole}
					userOrgID={userOrgID}
					authLoading={authLoading}
					handleEditEvent={handleEditEvent}
					handleShowDeleteConfirm={handleShowDeleteConfirm}
					timeZoneOptions={timeZoneOptions}
					serviceTypes={serviceTypes}
				/>

				{/* Create Event Modal */}
				<CreateEventModal
					show={showCreateModal}
					onHide={handleCloseCreateModal}
					createFormData={createFormData}
					organizations={organizations}
					userRole={userRole}
					userOrgID={userOrgID}
					handleCreateFormChange={handleCreateFormChange}
					handleCreateSubmit={handleCreateSubmit}
					createError={createError}
					createLoading={createLoading}
					timeZoneOptions={timeZoneOptions}
					serviceTypes={serviceTypes}
				/>

				{/* Delete Confirmation Modal */}
				<DeleteConfirmModal
					show={showDeleteConfirm}
					onHide={handleCloseDeleteConfirm}
					eventTitle={detailedEventData?.title}
					handleDeleteSubmit={handleDeleteSubmit}
					deleteLoading={deleteLoading}
					deleteError={deleteError}
				/>
		</div>
	</>
);
};

export default Calendar;
